import { useCallback, useEffect, useMemo, useState } from 'react';
import _get from 'lodash/get';
import { FetchIdsResponse } from '@lib/utils/getAllTableItemsIds';

type Args = {
  completionId: string | null;
  onChangeCompletionId: (id: string) => void;
  fetchIds?: () => Promise<FetchIdsResponse<unknown>>;
};

export default function useTaskCompletionDetailsActions(args: Args) {
  const { completionId, onChangeCompletionId, fetchIds } = args;

  const [allIds, setAllIds] = useState<string[]>([]);
  const [idsLoading, setIdsLoading] = useState<boolean>(false);
  const [idsError, setIdsError] = useState<any>(null);

  useEffect(() => {
    if (!completionId || !fetchIds) return undefined;
    setIdsLoading(true);
    setIdsError(null);
    fetchIds()
      .then((res) => {
        const ids = (_get(res, 'data.data.collection', []) || [])
          .map((n: unknown) => _get(n, 'id', ''))
          .filter((id) => !!id);
        setAllIds(ids);
      })
      .catch((e) => setIdsError(e))
      .finally(() => setIdsLoading(false));
    return undefined;
  }, [fetchIds, completionId]);

  const currentIndex = useMemo(
    () => (completionId ? allIds.indexOf(completionId) : -1),
    [allIds, completionId],
  );

  const selectByIndex = useCallback(
    (nextIndex: number) => {
      const { length } = allIds;
      if (!length) return;
      if (nextIndex < 0 || nextIndex >= length) return;
      const nextId = allIds[nextIndex];
      onChangeCompletionId(nextId);
    },
    [allIds, onChangeCompletionId],
  );

  const onNavigatePrev = useCallback(() => {
    if (currentIndex <= 0) {
      return;
    }
    selectByIndex(currentIndex - 1);
  }, [currentIndex, selectByIndex]);

  const onNavigateNext = useCallback(() => {
    if (currentIndex === -1) {
      if (allIds.length) onChangeCompletionId(allIds[0]);
      return;
    }
    if (currentIndex >= allIds.length - 1) return;
    selectByIndex(currentIndex + 1);
  }, [allIds, currentIndex, selectByIndex, onChangeCompletionId]);

  const canNavigatePrev = currentIndex > 0;
  const canNavigateNext =
    currentIndex === -1 ? allIds.length > 0 : currentIndex < allIds.length - 1;

  return {
    allIds,
    idsLoading,
    idsError,
    onNavigatePrev,
    onNavigateNext,
    canNavigatePrev,
    canNavigateNext,
  } as const;
}
